import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import WolfCrest from "./WolfCrest";

const TARGET = new Date("2026-11-03T07:00:00-06:00").getTime();

function useCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, TARGET - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

const lineAnim = (delay) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] },
});

function CountUnit({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <span className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tabular-nums leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-navy/70 mt-2 font-semibold">{label}</span>
    </div>
  );
}

export default function Hero({ onNavigate }) {
  const { days, hours, minutes, seconds } = useCountdown();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-navy noise" data-testid="hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero-bridge.jpg"
          alt="Four Bears Bridge over the blue waters of Lake Sakakawea on the Fort Berthold Reservation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/5" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-white/35" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center justify-center min-h-screen px-5 pt-32 md:pt-28 pb-28 text-center text-navy">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_55%_at_50%_52%,rgba(248,249,250,0.6),transparent_72%)]" />
        <h1 className="font-serif font-black tracking-tight leading-[0.95]" data-testid="hero-heading">
          <span className="block overflow-hidden">
            <motion.span {...lineAnim(0.65)} className="block text-4xl sm:text-5xl lg:text-7xl xl:text-8xl">DARWIN DAVID WILLIAMS</motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span {...lineAnim(0.8)} className="block text-2xl sm:text-4xl lg:text-5xl font-medium italic text-navy/85 mt-2">
              for MHA Nation
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span {...lineAnim(0.95)} className="block text-4xl sm:text-6xl lg:text-7xl mt-2">
              CHAIRMAN
            </motion.span>
          </span>
        </h1>

        <div className="overflow-hidden mt-6">
          <motion.p {...lineAnim(1.1)} className="text-lg md:text-xl italic text-navy font-medium max-w-2xl mx-auto">
            Darwin "Dave" Williams. Committed to our shared vision to restore power back to the People where it belongs.
          </motion.p>
        </div>

        <div className="overflow-hidden mt-5">
          <motion.p
            {...lineAnim(1.18)}
            className="text-[11px] md:text-sm uppercase tracking-[0.3em] font-bold text-navy/70"
            data-testid="hero-thanks"
          >
            Thank you to every primary voter — the finish line is in sight
          </motion.p>
        </div>

        <div className="overflow-hidden mt-10 mb-4">
          <motion.p
            {...lineAnim(1.25)}
            className="text-[11px] md:text-sm uppercase tracking-[0.35em] text-navy font-bold"
            data-testid="countdown-label"
          >
            MHA Nation General Election · November 3, 2026
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-start gap-5 sm:gap-8 md:gap-12"
          data-testid="countdown"
          aria-label="Countdown to general election"
        >
          <CountUnit value={days} label="Days" testid="countdown-days" />
          <span className="font-serif text-3xl md:text-5xl text-navy/50 -mt-1">:</span>
          <CountUnit value={hours} label="Hours" testid="countdown-hours" />
          <span className="font-serif text-3xl md:text-5xl text-navy/50 -mt-1">:</span>
          <CountUnit value={minutes} label="Min" testid="countdown-minutes" />
          <span className="font-serif text-3xl md:text-5xl text-navy/50 -mt-1">:</span>
          <CountUnit value={seconds} label="Sec" testid="countdown-seconds" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-20 flex items-center justify-center gap-4 md:gap-8 max-w-4xl"
          data-testid="hero-scripture"
        >
          <p className="font-serif italic text-navy/80 text-xs sm:text-sm md:text-base leading-snug text-right flex-1">
            "Let us not become weary in doing good,
          </p>
          <motion.div
            initial={{ scale: 0, rotate: -30, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-navy shrink-0"
          >
            <WolfCrest className="w-14 h-14 md:w-16 md:h-16 drop-shadow-[0_0_24px_rgba(255,255,255,0.45)]" />
          </motion.div>
          <p className="font-serif italic text-navy/80 text-xs sm:text-sm md:text-base leading-snug text-left flex-1">
            for at the proper time we will reap a harvest if we do not give up."
          </p>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.65 }}
          className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mt-4"
        >
          Galatians 6:9
        </motion.p>
      </motion.div>
    </section>
  );
}
